import { atom, createStore } from "jotai";
import { KbRecordItem } from "shared";
import { dayjs } from "utilz";

interface FileMeta {
  type: "folder" | "file";
  size?: number;
}

interface TreeNode<T> {
  id: string;
  label: React.ReactNode;
  type: string;
  icon?: React.ReactNode;
  data: T;
  children?: TreeNode<T>[];
}

export interface KbFilterType {
  date_created?: string[];
  last_modified?: string[];
  created_by?: string[];
}

// Helper function to get date range based on filter value
const getDateRange = (
  filterValue: string,
): { start: Date; end: Date } | null => {
  const now = dayjs();

  switch (filterValue) {
    case "today":
      return {
        start: now.startOf("day").toDate(),
        end: now.endOf("day").toDate(),
      };
    case "this_week":
      return {
        start: now.startOf("week").toDate(),
        end: now.endOf("week").toDate(),
      };
    case "this_month":
      return {
        start: now.startOf("month").toDate(),
        end: now.endOf("month").toDate(),
      };
    case "overall":
      return null; // No filtering for overall
    default:
      return null;
  }
};

// Helper function to check if an item matches the filters
const matchesFilters = (
  item: KbRecordItem,
  filters: KbFilterType,
  searchQuery?: string,
): boolean => {
  // If no filters and no search query, everything matches
  if ((!filters || Object.keys(filters).length === 0) && !searchQuery) {
    return true;
  }

  // Check search query (case-insensitive)
  if (searchQuery && searchQuery.trim()) {
    const itemName = item.name?.toLowerCase() || "";
    const query = searchQuery.toLowerCase().trim();
    if (!itemName.includes(query)) {
      return false;
    }
  }

  // Check date_created filter
  if (filters.date_created && filters.date_created.length > 0) {
    const filterValue = filters.date_created[0];
    const dateRange = getDateRange(filterValue);

    if (dateRange) {
      const itemDate = new Date(item.created_at);
      if (itemDate < dateRange.start || itemDate > dateRange.end) {
        return false;
      }
    }
    // If dateRange is null (overall), don't filter
  }

  // Check last_modified filter
  if (filters.last_modified && filters.last_modified.length > 0) {
    const filterValue = filters.last_modified[0];
    const dateRange = getDateRange(filterValue);

    if (dateRange) {
      const itemDate = new Date(item.updated_at);
      if (itemDate < dateRange.start || itemDate > dateRange.end) {
        return false;
      }
    }
    // If dateRange is null (overall), don't filter
  }

  // Check created_by filter
  if (filters.created_by && filters.created_by.length > 0) {
    if (!filters.created_by.includes(item.created_by)) {
      return false;
    }
  }

  return true;
};

// Recursive function to filter the tree
const filterKbTree = (
  items: KbRecordItem[],
  filters: KbFilterType,
  searchQuery?: string,
): KbRecordItem[] => {
  if ((!filters || Object.keys(filters).length === 0) && !searchQuery) {
    return items;
  }

  const filtered: KbRecordItem[] = [];

  for (const item of items) {
    // Recursively filter children
    const filteredChildren = item.children
      ? filterKbTree(item.children, filters, searchQuery)
      : [];

    // Include item if:
    // 1. It's a folder and has matching children, OR
    // 2. It matches the filters itself
    if (item.kb_type === "folder") {
      // Keep folder if it has any matching children
      if (filteredChildren.length > 0) {
        filtered.push({
          ...item,
          children: filteredChildren,
        });
      } else if (matchesFilters(item, filters, searchQuery)) {
        // Or if the folder itself matches
        filtered.push({
          ...item,
          children: [],
        });
      }
    } else {
      // For files, check if they match
      if (matchesFilters(item, filters, searchQuery)) {
        filtered.push(item);
      }
    }
  }

  return filtered;
};

export const kbStore = createStore();

// base atoms
const searchQueryAtom = atom("");
const sortByAtom = atom("name_asc");
const filtersAtom = atom<Record<string, any>>({});
const sortAtom = atom<string>("name_asc");
const selectedKbItemIdsAtom = atom<string[]>([]);
const kbFolderTreeAtom = atom<KbRecordItem[]>([]);
const displayModeAtom = atom<"grid" | "list">("grid");
const isPublishedAtom = atom<boolean>(false);
const hasPublishedVersionsAtom = atom<boolean>(false);
const recentlyCreatedFolderIdAtom = atom<string | null>(null);

// Helper function to sort items
const sortKbItems = (
  items: KbRecordItem[],
  sortBy: string,
  recentlyCreatedId: string | null = null,
): KbRecordItem[] => {
  const [column, direction] = sortBy.split("_");
  const isAsc = direction === "asc";

  const sorted = [...items].sort((a, b) => {
    // Always put recently created folder first
    if (recentlyCreatedId) {
      if (a.mapping_id === recentlyCreatedId) return -1;
      if (b.mapping_id === recentlyCreatedId) return 1;
    }

    let compareValue = 0;

    if (column === "name") {
      compareValue = a.name.localeCompare(b.name);
    } else if (column === "date") {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      compareValue = dateA - dateB;
    }

    return isAsc ? compareValue : -compareValue;
  });

  // Recursively sort children
  return sorted.map((item) => {
    if (item.children && item.children.length > 0) {
      return {
        ...item,
        children: sortKbItems(item.children, sortBy, recentlyCreatedId),
      };
    }
    return item;
  });
};

// Filtered and sorted folder tree
const filteredKbFolderTreeAtom = atom<KbRecordItem[]>((get) => {
  const tree = get(kbFolderTreeAtom);
  const filters = get(filtersAtom) as KbFilterType;
  const sortBy = get(sortByAtom);
  const searchQuery = get(searchQueryAtom);
  const recentlyCreatedId = get(recentlyCreatedFolderIdAtom);

  // First filter, then sort
  const filtered = filterKbTree(tree, filters, searchQuery);
  return sortKbItems(filtered, sortBy, recentlyCreatedId);
});

const folderMapAtom = atom<Map<string, KbRecordItem>>((get) =>
  createFolderMap(get(filteredKbFolderTreeAtom)),
);

// derived atoms
export const searchQueryStr = atom((get) => get(searchQueryAtom));
export const sortByStr = atom((get) => get(sortByAtom));
export const filtersObj = atom((get) => get(filtersAtom));
export const sortStr = atom((get) => get(sortAtom));
export const selectedKbItemIdsArr = atom((get) => get(selectedKbItemIdsAtom));
export const kbFolderTreeArr = atom((get) => get(kbFolderTreeAtom));
export const folderMap = atom((get) => get(folderMapAtom));
export const displayMode = atom((get) => get(displayModeAtom));
export const isPublished = atom((get) => get(isPublishedAtom));
export const hasPublishedVersions = atom((get) =>
  get(hasPublishedVersionsAtom),
);
export const recentlyCreatedFolderId = atom((get) =>
  get(recentlyCreatedFolderIdAtom),
);

export const hasActiveFiltersBoolean = atom((get) => {
  const searchQuery = get(searchQueryAtom);
  const sortBy = get(sortByAtom);
  const filters = get(filtersAtom);
  return (
    searchQuery !== "" ||
    sortBy !== "name_asc" ||
    Object.keys(filters).length > 0
  );
});
export const sortColumnAStr = atom((get) => {
  const sortBy = get(sortByAtom);
  return sortBy.split("_")[0];
});
export const sortDirectionStr = atom((get) => {
  const sortBy = get(sortByAtom);
  return sortBy.split("_")[1] as "asc" | "desc";
});

export const customerFolderList = atom((get) => {
  const folderTree = get(filteredKbFolderTreeAtom);
  return folderTree.filter((item) => item.is_customer_folder);
});

export const centralFolderList = atom((get) => {
  const folderTree = get(filteredKbFolderTreeAtom);
  return folderTree.filter((item) => !item.is_customer_folder);
});

// actions
export const setSearchQueryFn = atom(null, (_, set, value: string) => {
  set(searchQueryAtom, value);
});

export const setSortByFn = atom(null, (_, set, value: string) => {
  set(sortByAtom, value);
});

export const setFiltersFn = atom(null, (_, set, value: Record<string, any>) => {
  set(filtersAtom, value);
});

export const resetAllFn = atom(null, (_, set) => {
  set(searchQueryAtom, "");
  set(sortByAtom, "name_asc");
  set(filtersAtom, {});
  set(selectedKbItemIdsAtom, []);
});

export const resetSearchFn = atom(null, (_, set) => {
  set(searchQueryAtom, "");
});

export const resetFiltersFn = atom(null, (_, set) => {
  set(filtersAtom, {});
});

export const resetSortFn = atom(null, (_, set) => {
  set(sortByAtom, "name_asc");
});

export const setSelectedKbItemIdsFn = atom(null, (_, set, value: string[]) => {
  set(selectedKbItemIdsAtom, value);
});

export const toggleKbSelectionFn = atom(null, (get, set, mappingId: string) => {
  if (!mappingId) return;
  const currentIds = get(selectedKbItemIdsAtom);
  if (currentIds?.includes(mappingId)) {
    set(
      selectedKbItemIdsAtom,
      currentIds?.filter((id) => id !== mappingId),
    );
  } else {
    set(selectedKbItemIdsAtom, [...currentIds, mappingId]);
  }
});

export const clearSelectedKbItemIds = atom(null, (_, set) => {
  set(selectedKbItemIdsAtom, []);
});

export const setKbFolderTree = atom(null, (_, set, value: KbRecordItem[]) => {
  set(kbFolderTreeAtom, value);
});

// Conversion function
const convertKbItemsToTreeNodes = (items: KbRecordItem[]): any => {
  const folders = items.filter((item) => item.kb_type === "folder");

  return folders.map((item) => ({
    id: item.mapping_id,
    label: item.name,
    type: item.kb_type,
    parentId: item.parent_id,
    ...(item.children?.length > 0 && {
      children: convertKbItemsToTreeNodes(item.children),
    }),
  }));
};

export const customerFolderTreeNodes = atom((get) => {
  const customerFolders = get(customerFolderList);
  const data = convertKbItemsToTreeNodes(customerFolders);
  return data;
});

export const centralFolderTreeNodes = atom((get) => {
  const centralFolders = get(centralFolderList);
  let data = convertKbItemsToTreeNodes(centralFolders);

  return data;
});

const createFolderMap = (items: KbRecordItem[]) => {
  const itemMap = new Map();
  const stack = [...items];

  while (stack.length) {
    const node = stack.pop();
    if (!node) continue;
    itemMap.set(node.mapping_id, node);
    if (node.children && node.children.length) {
      // push children for later processing
      for (let i = 0; i < node.children.length; i++) {
        stack.push(node.children[i]);
      }
    }
  }
  return itemMap;
};

export const setDisplayMode = atom(null, (_, set, value: "grid" | "list") => {
  set(displayModeAtom, value);
});

export const setIsPublished = atom(
  null,
  (
    _,
    set,
    versions:
      | Array<{ record_visibility: string; is_live: boolean }>
      | undefined,
  ) => {
    const isPublished =
      versions?.some(
        (version) =>
          version.record_visibility === "public" && version.is_live === true,
      ) ?? false;
    set(isPublishedAtom, isPublished);

    const hasPublishedVersions =
      versions?.some((version) => version.record_visibility === "public") ??
      false;
    set(hasPublishedVersionsAtom, hasPublishedVersions);
  },
);

export const setRecentlyCreatedFolderId = atom(
  null,
  (_, set, value: string | null) => {
    set(recentlyCreatedFolderIdAtom, value);
  },
);

export const clearRecentlyCreatedFolderId = atom(null, (_, set) => {
  set(recentlyCreatedFolderIdAtom, null);
});

export const selectAllFilesInFolderFn = atom(
  null,
  (get, set, folderId: string | null) => {
    let children: KbRecordItem[] = [];

    if (!folderId) {
      // Global KB - use central folder list
      const centralFolders = get(centralFolderList);
      children = centralFolders[0]?.children || [];
    } else {
      // Regular folder - use folder map
      const folderMapValue = get(folderMapAtom);
      children = folderMapValue.get(folderId)?.children || [];
    }

    // Get only files (not folders)
    const fileIds = children
      .filter((item) => item.kb_type === "file")
      .map((item) => item.mapping_id);

    set(selectedKbItemIdsAtom, fileIds);
  },
);
